import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { ticketsAPI } from '../api/client';
import type { TicketDecision } from '../types';

interface DecisionLogModalProps {
  onClose: () => void;
  onOpenTicket: (id: number) => void;
}

export default function DecisionLogModal({ onClose, onOpenTicket }: DecisionLogModalProps) {
  const [entries, setEntries] = useState<TicketDecision[] | null>(null);

  useEffect(() => {
    ticketsAPI.decisionLog().then(setEntries).catch(() => setEntries([]));
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-navy">Decision log</h2>
          <button type="button" onClick={onClose} className="p-1.5 pointer-coarse:p-2.5 pointer-coarse:-m-1 rounded-lg hover:bg-gray-100 transition" aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {entries === null && <p className="text-sm text-gray-400">Loading…</p>}
          {entries?.length === 0 && (
            <p className="text-sm text-gray-400">No decisions recorded yet.</p>
          )}
          {entries?.map((entry) => (
            <div key={entry.id} className="border border-gray-100 rounded-xl p-3">
              <p className="text-xs text-gray-400">
                {new Date(entry.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                {' · '}{entry.decided_by_name || 'Someone'}
              </p>
              {entry.ticket_id ? (
                <button
                  type="button"
                  onClick={() => onOpenTicket(entry.ticket_id as number)}
                  className="text-sm font-semibold text-navy hover:underline text-left"
                >
                  {entry.ticket_title}
                </button>
              ) : (
                <p className="text-sm font-semibold text-navy">{entry.ticket_title}</p>
              )}
              {entry.question && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">Q: {entry.question}</p>}
              <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{entry.decision}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
